import { useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';

import { useTracking } from '@xing-com/crate-communication-tracking';
import { useLocation, useParams } from '@xing-com/crate-core-router';
import {
  getXingJobsDirectoryLetterJobRolePath,
  getXingJobsDirectoryLetterPath,
  getXingJobsDirectorySkillSkillPath,
} from '@xing-com/crate-core-routes';
import { Head } from '@xing-com/crate-master-directory-head';
import { deslugify } from '@xing-com/crate-master-directory-helpers';
import { useGetLocationsBySkill } from '@xing-com/crate-master-directory-hooks';
import {
  LinkList,
  MasterDirectoryDataBoundary,
} from '@xing-com/crate-master-directory-links';
import { SearchHeader } from '@xing-com/crate-master-directory-search-header';
import { useHost } from '@xing-com/crate-xinglet';

import * as S from './skill.styled';

type Params = {
  skill: string;
};

type MasterDirectoryLocationState = {
  from?: string;
};

const LocationsBySkillList: React.FC<{
  queryKey: number;
  skill: string;
}> = ({ queryKey, skill }) => {
  const { collection } = useGetLocationsBySkill({
    queryKey,
    skill,
  });

  if (collection.length === 0) {
    return null;
  }

  return (
    <>
      <S.Hero size="small" sizeConfined="medium">
        <FormattedMessage
          id="CRATE_MASTER_DIRECTORY_SKILL_HEADLINE"
          values={{
            skill: deslugify(skill),
          }}
        />
      </S.Hero>
      <LinkList entries={collection} />
    </>
  );
};

export const MasterDirectorySkillPage: React.FC = () => {
  const { isPreview } = useHost();
  const location = useLocation();
  const { track } = useTracking();
  const state: MasterDirectoryLocationState = location.state || {};

  const previousUrl = state?.from || '';
  const urlParts = previousUrl.split('/');

  const letter = urlParts[urlParts.length - 2];
  const jobRole = urlParts[urlParts.length - 1];

  const decodedJobRole = decodeURIComponent(jobRole);
  const jobRoleDesuglified = deslugify(decodedJobRole);

  const externalUrl = isPreview
    ? 'https://preview.xing.com'
    : 'https://www.xing.com';
  const { formatMessage } = useIntl();
  const { skill } = useParams<Params>();
  const skillDesluglified = deslugify(skill);
  const description = formatMessage(
    {
      id: 'CRATE_MASTER_DIRECTORY_SKILL_DESCRIPTION',
    },
    { skill: skillDesluglified }
  );

  const title = formatMessage(
    { id: 'CRATE_MASTER_DIRECTORY_SKILL_TITLE' },
    { skill: skillDesluglified }
  );
  const path = getXingJobsDirectorySkillSkillPath({
    skill: skill.toLocaleLowerCase(),
  }).toLocaleLowerCase();
  const canonicalUrl = `${externalUrl}${path}`;
  const breadcrumbItems = [];

  if (letter && jobRole) {
    const items = [
      {
        title: formatMessage({
          id: 'CRATE_MASTER_DIRECTORY_BREADCRUMB_HOME',
        }),
        url: getXingJobsDirectoryLetterPath({ letter: 'a' }),
      },
      {
        title: letter.toLocaleUpperCase(),
        url: getXingJobsDirectoryLetterPath({ letter }),
      },
      {
        title: jobRoleDesuglified,
        url: getXingJobsDirectoryLetterJobRolePath({
          letter,
          jobRole,
        }),
      },
      { title: skillDesluglified, url: '' },
    ];
    breadcrumbItems.push(...items);
  }

  useEffect(() => {
    track({
      application: 'wbm',
      eventSchema: 'basic',
      sentBy: 'directory',
      page: 'directory/skill',
      event: 'viewed_screen',
      type: 'nwt',
    });
  }, [track]);

  return (
    <>
      <Head
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        robots="noodp"
      />

      <SearchHeader />

      <S.Container>
        <S.SeoBreadcrumb items={breadcrumbItems} />
        <MasterDirectoryDataBoundary>
          {(queryKey) => (
            <LocationsBySkillList queryKey={queryKey} skill={skill} />
          )}
        </MasterDirectoryDataBoundary>
      </S.Container>
    </>
  );
};
